import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  Chip,
  Grid,
  Switch,
  FormControlLabel,
  Tooltip,
  IconButton,
  Fade,
  Badge,
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Star as StarIcon,
  CreditCard as CreditCardIcon,
  RocketLaunch as RocketIcon,
  WorkspacePremium as PremiumIcon,
  LocalOffer as OfferIcon,
  Info as InfoIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Share as ShareIcon,
  VerifiedUser as VerifiedIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { motion } from 'framer-motion';

// Componente estilizado para o card de plano
const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  borderRadius: '20px',
  '&:hover': {
    transform: 'translateY(-10px)',
  },
}));

// Componente estilizado para o ícone de verificação
const StyledCheckIcon = styled(CheckIcon)(({ theme }) => ({
  color: theme.palette.success.main,
}));

// Componente estilizado para o ícone de fechamento
const StyledCloseIcon = styled(CloseIcon)(({ theme }) => ({
  color: theme.palette.error.main,
}));

// Interface para os recursos dos planos
interface PlanFeatures {
  maxLeads: number;
  maxProducts: number;
  whatsappBot: boolean;
  emailMarketing: boolean;
  chatIA: boolean;
  multipleUsers: boolean;
  customReports: boolean;
  support: string;
  isTrial: boolean;
  trialDays: number;
  price?: number;
  description?: string;
}

// Interface para os planos
interface Plan {
  name: string;
  icon: React.ReactNode;
  color: string;
  gradient: string;
  features: PlanFeatures;
  isCurrentPlan: boolean;
  price?: number;
  popular: boolean;
  description: string;
  trialDays?: number;
  isTrial?: boolean;
  highlights: string[];
}

// Componente para os cards de planos
const PlanCards: React.FC = () => {
  const theme = useTheme();
  const userData = useSelector((state: RootState) => state.user.data);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  
  // Função para renderizar um recurso
  const renderFeature = (feature: boolean | number | string, label: string) => {
    if (typeof feature === 'boolean') {
      return (
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            {feature ? 
              <StyledCheckIcon /> : 
              <StyledCloseIcon />
            }
          </ListItemIcon>
          <ListItemText 
            primary={label} 
            primaryTypographyProps={{ 
              fontSize: '0.95rem',
              color: feature ? 'text.primary' : 'text.secondary',
              fontWeight: feature ? 'medium' : 'normal',
            }} 
          />
        </ListItem>
      );
    } else if (typeof feature === 'number') {
      if (feature === -1) {
        return (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <StyledCheckIcon />
            </ListItemIcon>
            <ListItemText 
              primary={`${label}: Ilimitado`} 
              primaryTypographyProps={{ 
                fontSize: '0.95rem',
                fontWeight: 'medium',
              }} 
            />
          </ListItem>
        );
      } else {
        return (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <StyledCheckIcon />
            </ListItemIcon>
            <ListItemText 
              primary={`${label}: ${feature}`} 
              primaryTypographyProps={{ 
                fontSize: '0.95rem',
                fontWeight: 'medium',
              }} 
            />
          </ListItem>
        );
      }
    } else {
      return (
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <StyledCheckIcon />
          </ListItemIcon>
          <ListItemText 
            primary={`${label}: ${feature}`} 
            primaryTypographyProps={{ 
              fontSize: '0.95rem',
              fontWeight: 'medium',
            }} 
          />
        </ListItem>
      );
    }
  };

  // Definição dos planos
  const plans: Plan[] = [
    {
      name: 'Free (Gratuito)',
      icon: <RocketIcon fontSize="large" />,
      color: '#6c757d',
      gradient: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
      features: userData.planFeatures,
      isCurrentPlan: userData.plan === 'Free',
      trialDays: 15,
      isTrial: userData.planFeatures.isTrial,
      description: 'Acesso completo por 15 dias. Após esse período, o acesso é limitado até a assinatura de um plano pago.',
      popular: false,
      highlights: [
        'WhatsApp IA Bot (básico)',
        'E-mail Response com IA (básico)',
        'Gestão de Redes Sociais (básico)',
        'Até 50 leads e 20 produtos'
      ]
    },
    {
      name: 'Intermediário',
      icon: <OfferIcon fontSize="large" />,
      color: theme.palette.primary.main,
      gradient: 'linear-gradient(135deg, #3f51b5 0%, #303f9f 100%)',
      features: {
        maxLeads: 200,
        maxProducts: 50,
        whatsappBot: true,
        emailMarketing: true,
        chatIA: false,
        multipleUsers: false,
        customReports: true,
        support: 'priority',
        isTrial: false,
        trialDays: 0,
        price: 59.90,
        description: 'Libera todas as funcionalidades do app. Limite de 50 produtos ou serviços cadastrados. Inclui suporte via WhatsApp 24h.'
      },
      isCurrentPlan: userData.plan === 'Basic',
      price: 59.90,
      popular: true,
      description: 'Ideal para corretores autônomos e pequenas imobiliárias que buscam automação e inteligência artificial.',
      highlights: [
        'WhatsApp IA Bot completo',
        'E-mail Response com IA avançado',
        'Automação de E-mails',
        'Até 200 leads e 50 produtos'
      ]
    },
    {
      name: 'Premium',
      icon: <PremiumIcon fontSize="large" />,
      color: theme.palette.secondary.main,
      gradient: 'linear-gradient(135deg, #f50057 0%, #c51162 100%)',
      features: {
        maxLeads: -1,
        maxProducts: -1,
        whatsappBot: true,
        emailMarketing: true,
        chatIA: true,
        multipleUsers: true,
        customReports: true,
        support: '24/7',
        isTrial: false,
        trialDays: 0,
        price: 99.90,
        description: 'Libera todas as funcionalidades + recursos premium. Cadastro ilimitado de produtos ou serviços. Inclui suporte via WhatsApp 24h.'
      },
      isCurrentPlan: userData.plan === 'Pro',
      price: 99.90,
      popular: false,
      description: 'Para imobiliárias, construtoras e incorporadoras que precisam de recursos avançados e capacidade ilimitada.',
      highlights: [
        'Leads e Produtos ilimitados',
        'Base de Conhecimento ilimitada',
        'Múltiplos usuários (até 10)',
        'Suporte prioritário 24/7'
      ]
    }
  ];

  // Calcular preço com desconto para plano anual
  const calculatePrice = (price: number) => {
    if (billingCycle === 'annual') {
      // 20% de desconto para planos anuais
      return price * 0.8;
    }
    return price;
  };

  return (
    <Box sx={{ width: '100%', py: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        mb: 4,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <FormControlLabel
          control={
            <Switch
              checked={billingCycle === 'annual'}
              onChange={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
              color="primary"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body1" sx={{ mr: 1 }}>
                Pagamento Anual
              </Typography>
              <Chip 
                label="Economize 20%" 
                size="small" 
                color="secondary" 
                sx={{ 
                  height: 22, 
                  fontSize: '0.7rem'
                }} 
              />
            </Box>
          }
          sx={{ mr: 2 }}
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Todos os planos incluem período de teste gratuito">
            <IconButton size="small" color="primary">
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">
            15 dias de teste grátis em todos os planos
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3} justifyContent="center" alignItems="stretch">
        {plans.map((plan) => (
          <Grid item xs={12} sm={6} md={4} key={plan.name} sx={{ display: 'flex' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: plans.indexOf(plan) * 0.1 }}
              style={{ width: '100%', height: '100%', display: 'flex' }}
            >
              <StyledCard
                elevation={plan.popular ? 6 : 1}
                sx={{
                  borderColor: plan.popular ? plan.color : 'transparent',
                  transform: plan.popular ? 'scale(1.02)' : 'scale(1)',
                  zIndex: plan.popular ? 2 : 1,
                  width: '100%'
                }}
              >
                {plan.popular && (
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      top: 0, 
                      right: 0, 
                      width: '150px', 
                      height: '150px', 
                      overflow: 'hidden',
                      zIndex: 0
                    }}
                  >
                    <Box 
                      sx={{ 
                        position: 'absolute', 
                        top: '-30px', 
                        right: '-30px', 
                        width: '120px', 
                        height: '120px', 
                        background: plan.gradient,
                        transform: 'rotate(45deg)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                      }}
                    >
                      MAIS POPULAR
                    </Box>
                  </Box>
                )}
                
                {plan.isCurrentPlan && (
                  <Badge 
                    sx={{ 
                      position: 'absolute', 
                      top: 16, 
                      right: 16,
                      zIndex: 1,
                    }}
                    badgeContent={
                      <Chip 
                        label="Plano Atual" 
                        color="primary" 
                        sx={{ 
                          fontWeight: 'bold',
                          borderRadius: '20px',
                          px: 1
                        }} 
                      />
                    }
                  />
                )}
                
                <Box 
                  sx={{ 
                    p: 3, 
                    pb: 2,
                    background: plan.gradient,
                    color: 'white',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box 
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.2)', 
                        borderRadius: '50%', 
                        p: 1.5,
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {plan.icon}
                    </Box>
                    <Typography variant="h5" component="div" fontWeight="bold">
                      {plan.name}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mt: 2 }}>
                    {plan.name.includes('Free') ? (
                      <Box>
                        <Typography variant="h4" component="div" fontWeight="bold">
                          Grátis
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5, opacity: 0.9 }}>
                          15 dias de acesso completo
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                          <Typography variant="h4" component="div" fontWeight="bold">
                            R$ {calculatePrice(plan.price || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="body1" sx={{ ml: 1, opacity: 0.9 }}>
                            /{billingCycle === 'monthly' ? 'mês' : 'mês*'}
                          </Typography>
                        </Box>
                        {billingCycle === 'annual' && (
                          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8 }}>
                            *Cobrança anual de R$ {(calculatePrice(plan.price || 0) * 12).toFixed(2)}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                          {plan.description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ p: 0.5, bgcolor: plan.color, color: 'white' }}>
                  <Typography variant="body2" align="center" fontWeight="medium">
                    Recursos Principais
                  </Typography>
                </Box>
                
                <CardContent sx={{ flexGrow: 1, py: 2, px: 3 }}>
                  <List sx={{ py: 0 }}>
                    {plan.highlights.map((highlight, idx) => (
                      <ListItem key={idx} sx={{ py: 1, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <VerifiedIcon sx={{ color: plan.color }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={highlight} 
                          primaryTypographyProps={{ 
                            fontSize: '0.95rem',
                            fontWeight: 'medium',
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mt: 2, 
                    mb: 1,
                    gap: 1,
                    flexWrap: 'wrap'
                  }}>
                    {plan.features.whatsappBot && (
                      <Tooltip title="WhatsApp IA Bot Incluído" placement="top">
                        <Chip 
                          icon={<WhatsAppIcon />} 
                          label="WhatsApp" 
                          size="small" 
                          sx={{ 
                            bgcolor: 'rgba(37, 211, 102, 0.1)', 
                            color: '#25d366',
                            borderColor: 'rgba(37, 211, 102, 0.2)',
                            '& .MuiChip-icon': { color: '#25d366' }
                          }} 
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    {plan.features.emailMarketing && (
                      <Tooltip title="E-mail Response com IA Incluído" placement="top">
                        <Chip 
                          icon={<EmailIcon />} 
                          label="E-mail" 
                          size="small" 
                          sx={{ 
                            bgcolor: 'rgba(66, 133, 244, 0.1)', 
                            color: '#4285f4',
                            borderColor: 'rgba(66, 133, 244, 0.2)',
                            '& .MuiChip-icon': { color: '#4285f4' }
                          }} 
                          variant="outlined"
                        />
                      </Tooltip>
                    )}
                    <Tooltip title="Gestão de Redes Sociais Incluída" placement="top">
                      <Chip 
                        icon={<ShareIcon />} 
                        label="Social" 
                        size="small" 
                        sx={{ 
                          bgcolor: 'rgba(225, 48, 108, 0.1)', 
                          color: '#e1306c',
                          borderColor: 'rgba(225, 48, 108, 0.2)',
                          '& .MuiChip-icon': { color: '#e1306c' }
                        }} 
                        variant="outlined"
                      />
                    </Tooltip>
                  </Box>
                </CardContent>
                
                <Divider />
                
                <CardActions sx={{ p: 3, pt: 2 }}>
                  {plan.name.includes('Free') ? (
                    <Button 
                      fullWidth 
                      variant={plan.isCurrentPlan ? "outlined" : "contained"} 
                      color="primary"
                      disabled={plan.isCurrentPlan}
                      size="large"
                      sx={{ 
                        py: 1.5,
                        fontWeight: 'bold',
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: plan.isCurrentPlan ? 'none' : '0 4px 14px rgba(0, 0, 0, 0.1)',
                      }}
                      endIcon={<ArrowForwardIcon />}
                    >
                      {plan.isCurrentPlan ? 'Plano Atual' : 'Começar Agora'}
                    </Button>
                  ) : (
                    <Button 
                      fullWidth 
                      variant={plan.isCurrentPlan ? "outlined" : "contained"} 
                      color={plan.name === 'Premium' ? "secondary" : "primary"}
                      disabled={plan.isCurrentPlan}
                      startIcon={<CreditCardIcon />}
                      size="large"
                      sx={{ 
                        py: 1.5,
                        fontWeight: 'bold',
                        borderRadius: '10px',
                        textTransform: 'none',
                        fontSize: '1rem',
                        background: plan.isCurrentPlan ? 'transparent' : plan.gradient,
                        boxShadow: plan.isCurrentPlan ? 'none' : '0 4px 14px rgba(0, 0, 0, 0.15)',
                        '&:hover': {
                          background: plan.isCurrentPlan ? 'transparent' : plan.gradient,
                          opacity: 0.9,
                          boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
                        }
                      }}
                    >
                      {plan.isCurrentPlan ? 'Plano Atual' : 'Assinar Agora'}
                    </Button>
                  )}
                </CardActions>
              </StyledCard>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default PlanCards;
